/**
 * JIRA Service
 *
 * Frontend service for interacting with JIRA integration endpoints.
 * Handles configuration, project mapping, requirement sync, and webhooks.
 */

import axios from 'axios';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:5001/api';

// ============================================================================
// JIRA Configuration (Company-level)
// ============================================================================

export interface JiraConfiguration {
  id: string;
  company_id: string;
  jira_domain: string;
  jira_email: string;
  webhook_secret?: string;
  is_active: boolean;
  created_at: Date;
  updated_at: Date;
}

export interface CreateJiraConfigInput {
  jira_domain: string;
  jira_email: string;
  jira_api_token: string;
  webhook_secret?: string;
}

export async function getJiraConfigurationStatus(token: string) {
  const response = await axios.get(`${API_BASE}/jira/configuration/status`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
}

export async function getJiraConfiguration(token: string) {
  const response = await axios.get(`${API_BASE}/jira/configuration`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
}

export async function createOrUpdateJiraConfiguration(
  data: CreateJiraConfigInput,
  token: string
) {
  const response = await axios.post(`${API_BASE}/jira/configuration`, data, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
}

export async function testJiraConnection(token: string) {
  const response = await axios.post(
    `${API_BASE}/jira/configuration/test`,
    {},
    {
      headers: { Authorization: `Bearer ${token}` }
    }
  );
  return response.data;
}

export async function getJiraProjects(token: string) {
  const response = await axios.get(`${API_BASE}/jira/configuration/projects`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
}

export async function getJiraIssueTypes(projectKey: string, token: string) {
  const response = await axios.get(
    `${API_BASE}/jira/configuration/issue-types/${projectKey}`,
    {
      headers: { Authorization: `Bearer ${token}` }
    }
  );
  return response.data;
}

export async function getJiraPriorities(token: string) {
  const response = await axios.get(`${API_BASE}/jira/configuration/priorities`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
}

export async function deleteJiraConfiguration(token: string) {
  const response = await axios.delete(`${API_BASE}/jira/configuration`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
}

// ============================================================================
// JIRA Project Mapping
// ============================================================================

export interface JiraProjectMapping {
  id: string;
  company_id: string;
  project_id: string;
  jira_configuration_id: string;
  jira_project_key: string;
  jira_project_id: string;
  jira_project_name: string;
  default_issue_type: string;
  default_priority: string;
  auto_sync_enabled: boolean;
  is_active: boolean;
  created_at: Date;
  updated_at: Date;
}

export interface CreateProjectMappingInput {
  project_id: string;
  jira_project_key: string;
  default_issue_type?: string;
  default_priority?: string;
  auto_sync_enabled?: boolean;
}

export async function getProjectMappings(projectId: string, token: string) {
  const response = await axios.get(
    `${API_BASE}/projects/${projectId}/jira/mapping`,
    {
      headers: { Authorization: `Bearer ${token}` }
    }
  );
  return response.data;
}

export async function createProjectMapping(
  projectId: string,
  data: CreateProjectMappingInput,
  token: string
) {
  const response = await axios.post(
    `${API_BASE}/projects/${projectId}/jira/mapping`,
    data,
    {
      headers: { Authorization: `Bearer ${token}` }
    }
  );
  return response.data;
}

export async function updateProjectMapping(
  projectId: string,
  mappingId: string,
  data: Partial<CreateProjectMappingInput>,
  token: string
) {
  const response = await axios.post(
    `${API_BASE}/projects/${projectId}/jira/mapping`,
    data,
    {
      headers: { Authorization: `Bearer ${token}` }
    }
  );
  return response.data;
}

export async function deleteProjectMapping(
  projectId: string,
  mappingId: string,
  token: string
) {
  const response = await axios.delete(
    `${API_BASE}/projects/${projectId}/jira/mapping`,
    {
      headers: { Authorization: `Bearer ${token}` }
    }
  );
  return response.data;
}

// ============================================================================
// Requirement Sync
// ============================================================================

export interface JiraRequirementSync {
  id: string;
  requirement_id: string;
  project_id: string;
  company_id: string;
  jira_project_mapping_id: string;
  jira_issue_key: string;
  jira_issue_id: string;
  jira_issue_url: string;
  sync_direction: 'push' | 'pull' | 'bidirectional';
  sync_status: 'synced' | 'pending' | 'error';
  last_sync_direction: 'push' | 'pull' | null;
  nivoon_last_updated_at: Date;
  jira_last_updated_at: Date;
  error_message: string | null;
  retry_count: number;
  is_active: boolean;
  created_at: Date;
  updated_at: Date;
}

export interface PushRequirementInput {
  issue_type?: string;
  priority?: string;
  force?: boolean;
}

export interface BulkPushInput {
  requirement_ids?: string[];
  issue_type?: string;
  priority?: string;
  skip_synced?: boolean;
}

export async function pushRequirementToJira(
  projectId: string,
  requirementId: string,
  data: PushRequirementInput,
  token: string
) {
  const response = await axios.post(
    `${API_BASE}/projects/${projectId}/requirements/${requirementId}/jira/push`,
    data,
    {
      headers: { Authorization: `Bearer ${token}` }
    }
  );
  return response.data;
}

export async function bulkPushRequirements(
  projectId: string,
  data: BulkPushInput,
  token: string
) {
  const response = await axios.post(
    `${API_BASE}/projects/${projectId}/jira/push`,
    data,
    {
      headers: { Authorization: `Bearer ${token}` }
    }
  );
  return response.data;
}

export async function getSyncStatus(projectId: string, token: string) {
  const response = await axios.get(
    `${API_BASE}/projects/${projectId}/jira/sync-status`,
    {
      headers: { Authorization: `Bearer ${token}` }
    }
  );
  return response.data;
}

export async function unlinkRequirement(
  projectId: string,
  requirementId: string,
  token: string
) {
  const response = await axios.delete(
    `${API_BASE}/projects/${projectId}/requirements/${requirementId}/jira/sync`,
    {
      headers: { Authorization: `Bearer ${token}` }
    }
  );
  return response.data;
}

// ============================================================================
// Webhook Logs (Platform Admin only)
// ============================================================================

export interface WebhookLog {
  id: string;
  jira_configuration_id: string;
  company_id: string;
  event_type: string;
  jira_issue_key: string | null;
  jira_issue_id: string | null;
  webhook_payload: any;
  processing_status: 'pending' | 'processed' | 'failed';
  processed_at: Date | null;
  error_message: string | null;
  created_at: Date;
}

export async function getWebhookLogs(
  limit: number = 50,
  offset: number = 0,
  token: string
) {
  const response = await axios.get(`${API_BASE}/webhooks/jira/logs`, {
    headers: { Authorization: `Bearer ${token}` },
    params: { limit, offset }
  });
  return response.data;
}

export async function getWebhookLogDetails(logId: string, token: string) {
  const response = await axios.get(`${API_BASE}/webhooks/jira/logs/${logId}`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
}

// ============================================================================
// JIRA Board
// ============================================================================

export interface JiraBoardData {
  boardId: number;
  boardName: string;
  projectKey: string;
  columns: string[];
  issuesByStatus: Record<string, any[]>;
  totalIssues: number;
}

/**
 * Get JIRA board data for a project
 * GET /api/projects/:projectId/jira/board
 */
export async function getBoardData(projectId: string, token: string): Promise<JiraBoardData> {
  const response = await axios.get(
    `${API_BASE}/projects/${projectId}/jira/board`,
    {
      headers: { Authorization: `Bearer ${token}` }
    }
  );
  return response.data.data;
}
